import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface User {
  id: string;
  email?: string;
  username: string | null;
  full_name: string | null;
  role: string;
  created_at: string;
  updated_at: string;
  avatar_url?: string;
}

export interface ContentNode {
  id: string;
  title: string;
  content?: string;
  discipline?: string;
  difficulty_level?: number;
  node_type?: string;
  estimated_study_time?: number;
  status: 'draft' | 'published' | 'archived';
  author_id: string;
  created_at: string;
  updated_at: string;
  type?: 'content' | 'lesson' | 'exercise';
}

export interface LearningPath {
  id: string;
  title: string;
  description?: string;
  nodes: ContentNode[];
  total_duration_minutes: number;
  difficulty_level: number;
  completion_percentage: number;
  created_at: string;
  updated_at: string;
}

export interface UserProgress {
  id: string;
  user_id: string;
  content_id: string;
  status: 'not_started' | 'in_progress' | 'completed';
  progress_percentage: number;
  time_spent_minutes: number;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface Discipline {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  parent_id?: string;
  created_at: string;
  updated_at: string;
}

class DataService {
  // User management
  async getUsers(): Promise<User[]> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      return [];
    }
  }

  async getUserById(userId: string): Promise<User | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching user:', error);
      return null;
    }
  }

  async deleteUser(userId: string): Promise<boolean> {
    try {
      // Delete user-related data first
      await Promise.all([
        supabase.from('user_discipline_interests').delete().eq('user_id', userId),
        supabase.from('user_progress').delete().eq('user_id', userId),
        supabase.from('user_preferences').delete().eq('user_id', userId),
        supabase.from('bookmarks').delete().eq('user_id', userId),
      ]);

      // Delete profile
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);

      if (profileError) throw profileError;

      // Delete from auth (requires admin privileges)
      const { error: authError } = await supabase.auth.admin.deleteUser(userId);
      if (authError) {
        console.warn('Could not delete from auth.users (may require admin privileges):', authError);
      }

      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
      return false;
    }
  }

  async resetUserInterests(userId: string): Promise<boolean> {
    try {
      await Promise.all([
        supabase.from('user_discipline_interests').delete().eq('user_id', userId),
        supabase.from('user_progress').delete().eq('user_id', userId),
        supabase.from('user_preferences').delete().eq('user_id', userId),
      ]);

      toast.success('User interests and progress reset successfully');
      return true;
    } catch (error) {
      console.error('Error resetting user interests:', error);
      toast.error('Failed to reset user interests');
      return false;
    }
  }

  // Content management
  async getContentNodes(filters?: {
    discipline?: string;
    status?: string;
    author_id?: string;
    limit?: number;
  }): Promise<ContentNode[]> {
    try {
      let query = supabase
        .from('content_nodes')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.discipline) {
        query = query.eq('discipline', filters.discipline);
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.author_id) {
        query = query.eq('author_id', filters.author_id);
      }
      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching content nodes:', error);
      return [];
    }
  }

  async getContentNodeById(nodeId: string): Promise<ContentNode | null> {
    try {
      const { data, error } = await supabase
        .from('content_nodes')
        .select('*')
        .eq('id', nodeId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching content node:', error);
      return null;
    }
  }

  async createContentNode(node: Partial<ContentNode>): Promise<ContentNode | null> {
    try {
      const { data, error } = await supabase
        .from('content_nodes')
        .insert([node])
        .select()
        .single();

      if (error) throw error;
      toast.success('Content created successfully');
      return data;
    } catch (error) {
      console.error('Error creating content node:', error);
      toast.error('Failed to create content');
      return null;
    }
  }

  async updateContentNode(nodeId: string, updates: Partial<ContentNode>): Promise<ContentNode | null> {
    try {
      const { data, error } = await supabase
        .from('content_nodes')
        .update(updates)
        .eq('id', nodeId)
        .select()
        .single();

      if (error) throw error;
      toast.success('Content updated successfully');
      return data;
    } catch (error) {
      console.error('Error updating content node:', error);
      toast.error('Failed to update content');
      return null;
    }
  }

  async deleteContentNode(nodeId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('content_nodes')
        .delete()
        .eq('id', nodeId);

      if (error) throw error;
      toast.success('Content deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting content node:', error);
      toast.error('Failed to delete content');
      return false;
    }
  }

  // Learning paths
  async getLearningPaths(): Promise<LearningPath[]> {
    try {
      const { data, error } = await supabase
        .from('learning_paths')
        .select(`
          *,
          learning_path_nodes!inner(
            sequence_order,
            content_nodes!inner(*)
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to match our interface
      return (data || []).map(path => ({
        ...path,
        nodes: path.learning_path_nodes
          ?.sort((a: any, b: any) => a.sequence_order - b.sequence_order)
          ?.map((lpn: any) => lpn.content_nodes) || []
      }));
    } catch (error) {
      console.error('Error fetching learning paths:', error);
      return [];
    }
  }

  // User progress
  async getUserProgress(userId: string, contentId?: string): Promise<UserProgress[]> {
    try {
      let query = supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });

      if (contentId) {
        query = query.eq('content_id', contentId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user progress:', error);
      return [];
    }
  }

  async updateUserProgress(userId: string, contentId: string, progress: Partial<UserProgress>): Promise<UserProgress | null> {
    try {
      const { data, error } = await supabase
        .from('user_progress')
        .upsert({
          user_id: userId,
          content_id: contentId,
          ...progress,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating user progress:', error);
      return null;
    }
  }

  // Disciplines
  async getDisciplines(): Promise<Discipline[]> {
    try {
      const { data, error } = await supabase
        .from('disciplines')
        .select('*')
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching disciplines:', error);
      return [];
    }
  }

  async getUserInterests(userId: string): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from('user_discipline_interests')
        .select(`
          disciplines!inner(name)
        `)
        .eq('user_id', userId);

      if (error) throw error;
      return (data || []).map((item: any) => item.disciplines.name);
    } catch (error) {
      console.error('Error fetching user interests:', error);
      return [];
    }
  }

  async updateUserInterests(userId: string, disciplineIds: string[]): Promise<boolean> {
    try {
      // Delete existing interests
      await supabase
        .from('user_discipline_interests')
        .delete()
        .eq('user_id', userId);

      // Insert new interests
      if (disciplineIds.length > 0) {
        const interests = disciplineIds.map(disciplineId => ({
          user_id: userId,
          discipline_id: disciplineId
        }));

        const { error } = await supabase
          .from('user_discipline_interests')
          .insert(interests);

        if (error) throw error;
      }

      return true;
    } catch (error) {
      console.error('Error updating user interests:', error);
      return false;
    }
  }

  // Search functionality
  async searchContent(query: string, filters?: {
    discipline?: string;
    difficulty?: number;
    limit?: number;
  }): Promise<ContentNode[]> {
    try {
      let dbQuery = supabase
        .from('content_nodes')
        .select('*')
        .eq('status', 'published')
        .or(`title.ilike.%${query}%,content.ilike.%${query}%`)
        .order('created_at', { ascending: false });

      if (filters?.discipline) {
        dbQuery = dbQuery.eq('discipline', filters.discipline);
      }
      if (filters?.difficulty) {
        dbQuery = dbQuery.eq('difficulty_level', filters.difficulty);
      }
      if (filters?.limit) {
        dbQuery = dbQuery.limit(filters.limit);
      }

      const { data, error } = await dbQuery;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching content:', error);
      return [];
    }
  }

  // Analytics and stats
  async getSystemStats(): Promise<{
    totalUsers: number;
    totalContent: number;
    totalViews: number;
    systemHealth: number;
  }> {
    try {
      const [usersResult, contentResult] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('content_nodes').select('id', { count: 'exact', head: true })
      ]);

      return {
        totalUsers: usersResult.count || 0,
        totalContent: contentResult.count || 0,
        totalViews: 0, // Would need to implement view tracking
        systemHealth: 99.9 // Would need to implement health monitoring
      };
    } catch (error) {
      console.error('Error fetching system stats:', error);
      return {
        totalUsers: 0,
        totalContent: 0,
        totalViews: 0,
        systemHealth: 0
      };
    }
  }
}

export const dataService = new DataService(); 